import React from 'react';
import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { useAdminAuth } from './AdminAuth';
import { useSupabase } from '../../supabaseAuth';
import AuditLogPage from './AuditLogPage';
import UsersPage from './UsersPage';
import SubscriptionsPage from './SubscriptionsPage';

// Admin Layout Component with Router-aware navigation
export const AdminLayout = ({ children }) => {
  const { logout } = useAdminAuth();
  const { user } = useSupabase();
  const location = useLocation();

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const pages = [
    { id: 'audit', label: 'Audit Log', icon: '📋', path: '/midas/audit' },
    { id: 'users', label: 'Users', icon: '👥', path: '/midas/users' },
    { id: 'subscriptions', label: 'Subscriptions', icon: '💰', path: '/midas/subscriptions' }
  ];

  // Determine current page based on URL
  const getCurrentPage = () => {
    const currentPath = location.pathname;
    const page = pages.find(p => p.path === currentPath);
    return page ? page.id : 'audit';
  };

  const currentPage = getCurrentPage();

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow-sm">
        <div className="container-fluid">
          <div className="navbar-brand fw-bold fs-3">
            🏛️ MIDAS
            <small className="fs-6 ms-2 opacity-75">Admin Interface</small>
          </div>
          
          <div className="d-flex align-items-center">
            <span className="text-light me-3">
              <i className="bi bi-person-circle me-1"></i>
              {user?.email || 'Administrator'}
            </span>
          </div>
        </div>
      </nav>

      {/* Navigation Tabs */}
      <div className="bg-white border-bottom">
        <div className="container-fluid">
          <ul className="nav nav-tabs border-0">
            {pages.map((page) => (
              <li className="nav-item" key={page.id}>
                <Link
                  to={page.path}
                  className={`nav-link ${currentPage === page.id ? 'active' : ''}`}
                >
                  <span className="me-2">{page.icon}</span>
                  {page.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid py-4">
        {children}
      </div>

      {/* Footer */}
      <footer className="bg-white border-top mt-auto py-3">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-md-6">
              <small className="text-muted">
                © 2025 Auric Admin Interface. All rights reserved.
              </small>
            </div>
            <div className="col-md-6 text-md-end">
              <small className="text-muted">
                Last updated: {new Date().toLocaleString()}
              </small>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Admin Router Component with proper routes
export const AdminRouter = () => {
  return (
    <AdminLayout>
      <Routes>
        <Route path="audit" element={<AuditLogPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="" element={<Navigate to="audit" replace />} />
        <Route path="*" element={<Navigate to="audit" replace />} />
      </Routes>
    </AdminLayout>
  );
};

// Main Admin Dashboard Component (legacy compatibility)
export const AdminDashboard = () => {
  return <AdminRouter />;
};

export default AdminRouter;
